<script setup lang="ts">
import {computed, inject, ref, shallowRef, watch} from "vue";
import SideBarUtils from "@/utils/common/SideBarUtils";
import {UIMenuGroup, UIMenuItem} from "@/utils/UIConstants/UIMenuGroup";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {storeToRefs} from "pinia";
import Translator from "@/utils/common/Translator.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {sideBarContext} from "@/utils/presenters/interfaces.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import ListViewToolbarUtils from "@/utils/common/ListViewToolbarUtils.ts";
import {refDebounced} from "@vueuse/core";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import {useRoute} from "vue-router";
import BlockUtils from "@/utils/common/BlockUtils.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";

const {barsDarkMode, showSidebarMenuIconColors} = storeToRefs(useNamaFlagsStore());
const context = inject(sideBarContext, {
  searchValue: ref(),
  panel: ref("home"),
  displayedMenuGroups: computed(() => []),
  searchMenuGroups: computed(() => []),
  currentGroup: ref(null),
  currentSubGroup: ref(null)
});

const debouncedSearchValue = refDebounced(context.searchValue, 200);
const searchMenuGroups = shallowRef<UIMenuGroup[]>([]);
const menuGroupsToDisplay = shallowRef<UIMenuGroup[]>([]);
const expandedSearchSubGroups = ref<Record<string, boolean>>({});
const expandedSubGroups = ref<Record<string, boolean>>({});
const currentItemKey = ref<string | null | undefined>(null);
const previousPanel = ref<string>("home");

function updateMenuGroups() {
  const map = new Map<string, UIMenuGroup>();
  context.displayedMenuGroups.value.forEach(g => {
    if (g.groupData.id)
      map.set(g.groupData.id, g)
  });
  const groups = Array.from(map.values());
  const currentIds = menuGroupsToDisplay.value.map(g => g.groupData.id).join(',');
  const newIds = groups.map(g => g.groupData.id).join(',');

  if (currentIds !== newIds) {
    menuGroupsToDisplay.value = groups;
  }
}

function updateSearchMenuGroups() {
  const map = new Map<string, UIMenuGroup>();
  context.searchMenuGroups.value.forEach(g => {
    if (g.groupData.id)
      map.set(g.groupData.id, g)
  });
  const groups = Array.from(map.values());
  const currentIds = searchMenuGroups.value.map(g => g.groupData.id).join(',');
  const newIds = groups.map(g => g.groupData.id).join(',');
  if (currentIds !== newIds) {
    searchMenuGroups.value = groups;
  }
}

watch(
  () => context.displayedMenuGroups.value,
  updateMenuGroups,
  {immediate: true}
);

watch(
  () => context.searchMenuGroups.value,
  updateSearchMenuGroups,
  {immediate: true}
);

watch(debouncedSearchValue, (searchValue) => {
  if (ObjectChecker.isNotEmptyOrNull(searchValue)) {
    if (context.panel.value !== "searchPanel") {
      previousPanel.value = context.panel.value;
    }
    context.panel.value = "searchPanel";
    for (const group of searchMenuGroups.value) {
      for (const subGroup of group.subGroups) {
        if (subGroup.subGroupData.code)
          expandedSearchSubGroups.value[subGroup.subGroupData.code] = true;
      }
    }
  } else if (useNamaFlagsStore().sidebarMenuMode.value !== NamaCommon.sideBarMenuModes.FOCUSED_TREE_MODE) {
    expandedSearchSubGroups.value = {};
    context.panel.value = previousPanel.value;
    updateCurrentItemHighlight();
  }
});
const route = useRoute()

watch(route, __ => {
  updateCurrentItemHighlight();
  setTimeout(()=>{
  scrollToTarget();
  },BlockUtils.qExpansionDuration)
})

function scrollToTarget() {
  const activeItem = document.querySelector(".is-current-item");
  if (!activeItem)
    return;
  GUIUtilities.scrollIntoView(activeItem);
}

function updateCurrentItemHighlight() {
  for (const group of menuGroupsToDisplay.value) {
    for (const subGroup of group.subGroups) {
      for (const item of subGroup.subItems) {
        if (context.isCurrent && context.isCurrent(item)) {
          currentItemKey.value = item.code || item.id;
          if (context.panel.value === group.groupData.code && subGroup.subGroupData.code)
            expandedSubGroups.value[subGroup.subGroupData.code] = true;
          return;
        }
      }
    }
  }
  currentItemKey.value = null;
}

watch(
  menuGroupsToDisplay,
  () => {
    if (menuGroupsToDisplay.value.length > 0) {
      updateCurrentItemHighlight();
    }
  },
  {immediate: true}
);

watch(
  () => context.panel.value,
  (newPanel) => {
    if (newPanel !== "searchPanel" && ObjectChecker.isEmptyOrNull(debouncedSearchValue.value)) {
      previousPanel.value = newPanel;
    }
  }
);

function isCurrentItem(item: UIMenuItem): boolean {
  return currentItemKey.value === (item.code || item.id);
}

function onBreadcrumbHomeClicked() {
  if (context.searchValue.value)
    context.searchValue.value = '';
  context.currentGroup.value = null;
  context.currentSubGroup.value = null;
  context.panel.value = "home";
}

function onItemClick(item: UIMenuItem, e?: Event) {
  currentItemKey.value = item.code || item.id;
  const savedPanel = context.panel.value;
  const savedGroup = context.currentGroup.value;
  if (context.goToEntity) {
    context.goToEntity(item, SideBarUtils.shouldOpenInNewTab(e));
    context.panel.value = savedPanel;
    context.currentGroup.value = savedGroup;
    context.currentSubGroup.value = null;
  }
}

function onBreadcrumbGroupClicked() {
  context.panel.value = context.currentGroup.value?.groupData.code!;
  context.currentSubGroup.value = null;
}

async function addEntity(item: UIMenuItem, e?: PointerEvent) {
  if (!SecurityUtils.userCanCreate(item.target?.targetEntity ?? ''))
    return;
  await ListViewToolbarUtils.onNewRecord(item.place?.query, SideBarUtils.shouldOpenInNewTab(e));
}
</script>
<template>
  <div class="tw-w-full tw-h-full tw-flex tw-flex-col dark:tw-text-namaFontColor-menu-dark dark:tw-bg-namaBackground-deep-dark">
    <div class="tw-p-2 tw-flex tw-flex-col">
      <q-breadcrumbs ref="breadcrumb" class="tw-pb-2">
        <q-breadcrumbs-el @click="onBreadcrumbHomeClicked" class="tw-cursor-pointer">
          <template #default>
            <div class="tw-flex tw-items-center">
              <NamaIcon :colored="showSidebarMenuIconColors" default-icon="nm-home" class="tw-size-5"
                        :class="{'active-primary':context.panel.value!=='home'}"/>
              &nbsp;
              <span v-text="Translator.translateId('main')" class="tw-leading-none"/>
            </div>
          </template>
        </q-breadcrumbs-el>
        <q-breadcrumbs-el @click="onBreadcrumbGroupClicked" class="tw-cursor-pointer"
                          v-if="ObjectChecker.isNotEmptyOrNull(context.currentGroup.value) && ObjectChecker.isEmptyOrNull(context.searchValue.value)">
          <template #default>
            <div class="tw-flex tw-items-center">
              <NamaIcon :colored="showSidebarMenuIconColors" :menu-item="context.currentGroup.value?.groupData" default-icon="nm-folders"
                        class="tw-size-5"
                        :class="{'active-primary':context.panel.value!==context.currentGroup.value?.groupData?.code}"/>
              &nbsp;
              <span v-text="Translator.translateMenuGroup(context.currentGroup.value.groupData)"/>
            </div>
          </template>
        </q-breadcrumbs-el>
      </q-breadcrumbs>
      <slot/>
    </div>
    <q-scroll-area :dir="Translator.LTR" :dark="barsDarkMode" class="fit">
      <q-tab-panels :dir="Translator.fetchDir()" v-model="context.panel.value" animated class="bg-transparent">
        <q-tab-panel name="home" class="no-padding">
          <q-list>
            <template :key="index" v-for="(group, index) in menuGroupsToDisplay">
              <q-item clickable @click.stop="context.mainGroupClick?.(group)"
                      active-class="my-menu-link" v-ripple>
                <q-item-section>
                  <div class="tw-flex tw-items-center tw-gap-3">
                    <NamaIcon :colored="showSidebarMenuIconColors" :menu-item="group.groupData" default-icon="nm-folders"/>
                    <span v-text="Translator.translateMenuGroup(group.groupData)"/>
                  </div>
                </q-item-section>
                <q-item-section avatar v-if="group.subGroups">
                  <q-icon :name="'chevron_'+(Translator.isLanguageRTL()?'left':'right')">
                  </q-icon>
                </q-item-section>
              </q-item>
              <q-separator class="dark:tw-bg-[#6C757D]"></q-separator>
            </template>
          </q-list>
        </q-tab-panel>
        <q-tab-panel name="searchPanel" class="no-padding">
          <q-list :dark="barsDarkMode">
            <template v-for="group in searchMenuGroups" :key="group.groupData.code">
              <q-expansion-item
                v-for="subGroup in group.subGroups"
                :key="subGroup.subGroupData.code"
                v-model="expandedSearchSubGroups[subGroup.subGroupData.code ?? '']"
                :dark="barsDarkMode"
                expand-icon-class="tw-text-inherit"
                :header-class="['tw-px-2', subGroup.subGroupData.code && expandedSearchSubGroups[subGroup.subGroupData.code] ? 'tw-bg-black/5 dark:tw-bg-white/5' : '']"
              >
                <template #header>
                  <q-item-section avatar class="tw-min-w-0">
                    <NamaIcon
                      :colored="showSidebarMenuIconColors"
                      :menu-item="group.groupData"
                      default-icon="nm-folders"
                      class="tw-size-5"
                    />
                  </q-item-section>
                  <q-item-section class="tw-text-sm">
                    {{ Translator.translateMenuGroup(group.groupData) }} - {{ Translator.translateMenuGroup(subGroup.subGroupData) }}
                  </q-item-section>
                </template>

                <template v-if="subGroup.subGroupData.code && expandedSearchSubGroups[subGroup.subGroupData.code]">
                  <div
                    v-for="item in subGroup.subItems"
                    :key="item.code || item.id"
                    class="tw-flex tw-my-1 tw-w-full tw-px-4 tw-gap-1 tw-items-center tw-justify-between"
                  >
                    <q-item
                      clickable
                      v-ripple
                      dense
                      :dark="barsDarkMode"
                      class="tw-rounded-md tw-px-3 tw-grow"
                      :class="{'tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-menu-dark is-current-item': isCurrentItem(item)}"
                      @click.stop="(event)=>onItemClick(item, event)"
                    >
                      <q-item-section avatar class="tw-min-w-0">
                        <NamaIcon
                          :colored="showSidebarMenuIconColors"
                          :menu-item="item"
                          :default-icon="item.iconCode || 'nm-monitoring'"
                          class="tw-size-[22px]"
                        />
                      </q-item-section>
                      <q-item-section class="tw-text-sm">
                        {{ SideBarUtils.translateMenuItem(item) }}
                      </q-item-section>
                    </q-item>
                    <a
                      v-if="SecurityUtils.userCanCreate(item.target?.targetEntity ?? '')"
                      @click.stop="(event) => addEntity(item, event)"
                      class="hover:tw-bg-gray-200 dark:hover:tw-bg-white/20 tw-p-1.5 tw-rounded-lg tw-cursor-pointer"
                    >
                      <NamaIcon class="tw-size-5 active-primary" default-icon="nm-add-record"/>
                    </a>
                  </div>
                </template>
              </q-expansion-item>
            </template>
          </q-list>
        </q-tab-panel>

        <q-tab-panel
          v-for="group in menuGroupsToDisplay"
          :key="group.groupData.code"
          :name="group.groupData.code"
          class="no-padding"
        >
          <q-list :dark="barsDarkMode">
            <q-expansion-item
              v-for="subGroup in group.subGroups"
              :key="subGroup.subGroupData.code"
              v-model="expandedSubGroups[subGroup.subGroupData.code??'']"
              :dark="barsDarkMode"
              expand-icon-class="tw-text-inherit"
              :header-class="['tw-px-2 tw-my-1', subGroup.subGroupData.code && expandedSubGroups[subGroup.subGroupData.code] ? 'tw-bg-black/5 dark:tw-bg-white/5' : '']"
            >
              <template #header>
                <q-item-section avatar class="tw-min-w-0">
                  <NamaIcon
                    :colored="showSidebarMenuIconColors"
                    :menu-item="subGroup.subGroupData"
                    :default-icon="subGroup.subGroupData.iconCode || 'nm-folder'"
                    class="tw-size-6"
                  />
                </q-item-section>
                <q-item-section class="tw-text-[15px]">
                  {{ Translator.translateMenuGroup(subGroup.subGroupData) }}
                </q-item-section>
              </template>

              <template v-if="subGroup.subGroupData.code && expandedSubGroups[subGroup.subGroupData.code]">
                <div
                  v-for="item in subGroup.subItems"
                  :key="item.code || item.id"
                  class="tw-flex tw-my-1 tw-w-full tw-px-4 tw-gap-1 tw-items-center tw-justify-between"
                >
                  <q-item
                    clickable
                    v-ripple
                    dense
                    :dark="barsDarkMode"
                    class="tw-rounded-md tw-px-3 tw-grow"
                    :class="{'tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-menu-dark is-current-item': isCurrentItem(item)}"
                    @click.stop="(event) => onItemClick(item, event)">
                    <q-item-section avatar class="tw-min-w-0">
                      <NamaIcon
                        :colored="showSidebarMenuIconColors"
                        :menu-item="item"
                        :default-icon="item.iconCode || 'nm-monitoring'"
                        class="tw-size-[22px]"
                      />
                    </q-item-section>
                    <q-item-section class="tw-text-sm">
                      {{ SideBarUtils.translateMenuItem(item) }}
                    </q-item-section>
                  </q-item>
                  <a
                    v-if="SecurityUtils.userCanCreate(item.target?.targetEntity ?? '')"
                    @click.stop="(event) => addEntity(item, event)"
                    class="hover:tw-bg-gray-200 dark:hover:tw-bg-white/20 tw-p-1.5 tw-rounded-lg tw-cursor-pointer"
                  >
                    <NamaIcon class="tw-size-5 active-primary" default-icon="nm-add-record"/>
                  </a>
                </div>
              </template>
            </q-expansion-item>
          </q-list>
        </q-tab-panel>
      </q-tab-panels>
    </q-scroll-area>
  </div>
</template>
